import React from 'react';
import { View, colors, Icon, Button, Heading } from '@fairygodboss/ui';
import window from 'global/window';
import { GridColumnWrapper } from './GridColumnWrapper';
import { useEventRegistration } from '../EventRegistrationContext';
import { AuthWrapper } from '../../../../pages/HomePage_v2/sections/components/AuthWrapper';
import {
    countDown,
    EVENT_IN_PROGRESS_BANNER,
    JOIN_LIVE_EVENT_BUTTON,
} from './helpers';

const StyledHeader = ({ children, ...others }) => (
    <Heading
        variant="headingMd"
        fontSize={{ _: '18px', sm: '26px' }}
        lineHeight={{ _: '18px', sm: '38px' }}
        {...others}
    >
        {children}
    </Heading>
);

const RegistrationButton = ({ text, onPress }) => (
    <Button
        mt={{ _: '8px', sm: '0px' }}
        ml="24px"
        onPress={onPress}
        rightIcon={<Icon variant="chevron_right_16" />}
        variant="primaryLink"
        size="large"
        fontSize={{ _: '14px', sm: '16px' }}
        lineHeight={{ _: '18px', sm: '20px' }}
        letterSpacint="-0.4px"
    >
        {text}
    </Button>
);

RegistrationButton.displayName = 'RegistrationButton';

export const CountdownBanner = () => {
    const {
        event,
        onRegistrationButtonClick,
        userRegistered,
        eventStarted,
        eventEnded,
        registrationExpired,
        countdownObject,
        registrationButtonText,
        setRegistrationButtonText,
        bannerText,
        setBannerText,
    } = useEventRegistration();

    const { days, hours, minutes } = countdownObject;

    const { startTime, eventType, registrationDeactivated } = event;

    const startTimeObject = countDown(startTime);
    let enableCountdown = true;

    if (
        eventType === 'Webinar' &&
        userRegistered &&
        !days &&
        !hours &&
        typeof minutes === 'number'
    ) {
        enableCountdown = false;
        setBannerText(EVENT_IN_PROGRESS_BANNER);
        setRegistrationButtonText(JOIN_LIVE_EVENT_BUTTON);
    }

    if (
        (eventType === 'Webinar' && startTimeObject && days > 6) ||
        registrationDeactivated
    ) {
        return null;
    }
    return (
        <GridColumnWrapper
            backgroundColor={colors.secondary[70]}
            gridAlignment="center"
            gridColumnSpacing={{ xs: 12, sm: 12, md: 12 }}
            px="12px"
            boxShadow="0px 4px 8px rgba(53, 53, 53, 0.2)"
            zIndex="3"
            position="sticky"
            top={{ _: '102px', oldmd: '68px' }}
        >
            <View
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={{
                    _: !registrationButtonText ? '48px' : '74px',
                    sm: '66px',
                }}
                color={colors.neutral[0]}
                fontSize={{ _: '12px', sm: '14px' }}
                lineHeight={{ _: '14px', sm: '18px' }}
                flexDirection={{ _: 'column', sm: 'row' }}
                css={{
                    h6: {
                        color: `${colors.neutral[0]} !important`,
                    },
                    h4: {
                        color: `${colors.neutral[0]} !important`,
                    },
                    button: {
                        backgroundColor: 'transparent',
                        ':hover': {
                            textDecorationColor: colors.primary[20],
                        },
                        span: {
                            color: colors.primary[20],
                            svg: {
                                height: '15px',
                                width: '15px',
                                path: {
                                    fill: colors.primary[20],
                                },
                            },
                        },
                    },
                }}
            >
                <View
                    display="flex"
                    justifyContent="center"
                    alignItems={{ _: 'end', sm: 'center' }}
                >
                    <View
                        fontSize={{ _: '12px', sm: '16px' }}
                        fontWeight="600"
                        lineHeight={{ _: '14px', sm: '24px' }}
                    >
                        {bannerText}
                    </View>
                    {enableCountdown &&
                    !eventStarted &&
                    !eventEnded &&
                    !(
                        registrationExpired &&
                        !userRegistered &&
                        eventType === 'Virtual Recruiting Event'
                    ) ? (
                        <>
                            <View
                                fontWeight="600"
                                fontSize={{ _: '12px', sm: '16px' }}
                                lineHeight={{ _: '14px', sm: '24px' }}
                                paddingLeft={{
                                    _: '0px',
                                    sm: '12px',
                                    md: '0px',
                                }}
                                css={{
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {eventType === 'Webinar' || userRegistered
                                    ? 'Event starts in'
                                    : 'Registration ends in'}
                            </View>
                            {days ? (
                                <>
                                    <StyledHeader
                                        ml={{ _: '12px', sm: '24px' }}
                                        mr={{ _: '4px', md: '8px' }}
                                    >
                                        {days}
                                    </StyledHeader>
                                    <View>Day{`${days < 2 ? '' : 's'}`}</View>
                                </>
                            ) : null}
                            {hours ? (
                                <>
                                    <StyledHeader
                                        ml={{ _: '12px', sm: '24px' }}
                                        mr={{ _: '4px', md: '8px' }}
                                    >
                                        {hours}
                                    </StyledHeader>
                                    <View>Hour{`${hours < 2 ? '' : 's'}`}</View>
                                </>
                            ) : null}
                            {minutes ? (
                                <>
                                    <StyledHeader
                                        ml={{ _: '12px', sm: '24px' }}
                                        mr={{ _: '4px', md: '8px' }}
                                    >
                                        {minutes}
                                    </StyledHeader>
                                    <View>
                                        Minute
                                        {`${
                                            minutes < 2 && !!minutes ? '' : 's'
                                        }`}
                                    </View>
                                </>
                            ) : null}
                        </>
                    ) : null}
                </View>

                {eventType === 'Webinar' &&
                !days &&
                !hours &&
                !minutes &&
                typeof minutes === 'number' &&
                bannerText !== EVENT_IN_PROGRESS_BANNER &&
                !userRegistered ? (
                    <View
                        fontWeight="600"
                        fontSize={{ _: '12px', sm: '16px' }}
                        lineHeight={{ _: '14px', sm: '24px' }}
                        paddingLeft={{
                            _: '0px',
                            sm: '12px',
                            md: '0px',
                        }}
                        css={{
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {EVENT_IN_PROGRESS_BANNER}
                    </View>
                ) : null}

                {registrationButtonText ? (
                    <View display="flex">
                        <AuthWrapper
                            hash="RegisterEvent"
                            cookie={{ name: 'REGISTER_EVENT', value: true }}
                            component={
                                <RegistrationButton
                                    text={registrationButtonText}
                                    onPress={onRegistrationButtonClick}
                                />
                            }
                            redirectUrl={window?.location?.pathname}
                            loginTrigger="register-event"
                        />
                    </View>
                ) : null}
            </View>
        </GridColumnWrapper>
    );
};

CountdownBanner.displayName = 'CountdownBanner';
